import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import classNames from 'classnames';
import { translationPropType } from '@eb/i18n';

import { PhabButton } from '@eb/eds-phab';
import ObjectFitImage from './ObjectFitImage';

import { Crop } from '@eb/eds-iconography';
import { Trash } from '@eb/eds-iconography';
import { Photo } from '@eb/eds-iconography';

/**
 * The preview element action button
 */
const PreviewElementAction = ({ iconType, onClick }) => {
    if (!onClick) {
        return null;
    }

    return (
        <div className="eds-image-uploader__preview-action eds-l-mar-all-2">
            <PhabButton iconType={iconType} onClick={onClick} />
        </div>
    );
};

/**
 * The preview element actions bar with Crop, Remove & Replace buttons
 */
const PreviewElementActions = ({
    onCrop,
    onRemove,
    onReplace,
    showActions,
}) => {
    if (!showActions) {
        return null;
    }

    return (
        <div className="eds-image-uploader__preview-actions eds-fx--fade-in eds-fx--delay-1">
            <PreviewElementAction iconType={<Crop />} onClick={onCrop} />
            <PreviewElementAction iconType={<Trash />} onClick={onRemove} />
            <PreviewElementAction iconType={<Photo />} onClick={onReplace} />
        </div>
    );
};

/**
 * The element shown when the uploader is populated with an image
 */
export default class PreviewElement extends PureComponent {
    static propTypes = {
        /**
         * Image url of the image for preview.
         */
        url: PropTypes.string,

        /**
         * Image alt text for the image element.
         */
        altText: translationPropType,

        /**
         * Function that's invoked when user clicks on crop button.
         */
        onCrop: PropTypes.func,

        /**
         * Function that's invoked when user clicks on trash button.
         */
        onRemove: PropTypes.func,

        /**
         * Function that's invoked when user clicks on image button.
         */
        onReplace: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            onTouch: null,
            onHover: false,
        };
    }

    _handleTouch() {
        this.setState(({ onTouch: prevOnTouch }) => ({
            onTouch: !prevOnTouch,
        }));
    }

    _handleMouseOver() {
        this.setState({
            onHover: true,
        });
    }

    _handleMouseLeave() {
        this.setState({
            onHover: false,
        });
    }

    _showActions() {
        // User should only be able to see preview action buttons by
        // 1) Touch event on mobile browsers
        // 2) Hover event on desktop browsers
        let showActions;

        if (this.state.onTouch !== null) {
            showActions = this.state.onTouch;
        } else {
            showActions = this.state.onHover;
        }

        return showActions;
    }

    render() {
        const { url, altText, onRemove, onReplace, onCrop } = this.props;

        const showActions = this._showActions();

        const previewBackgroundClassName = classNames(
            {
                'eds-image-uploader__preview-action-background-hidden': !showActions,
            },
            'eds-image-uploader__preview-action-background',
            'eds-fx--fade-in',
        );

        return (
            <div
                className="eds-image-uploader__preview"
                onMouseOver={this._handleMouseOver.bind(this)}
                onMouseLeave={this._handleMouseLeave.bind(this)}
                onFocus={noop()}
                data-spec="image-uploader-preview"
            >
                <ObjectFitImage
                    imageSrc={url}
                    imageAlt={altText}
                    imageClassName="eds-image-uploader__preview-image"
                    data-spec="image-uploader-preview-image"
                />
                <div
                    className={previewBackgroundClassName}
                    onTouchStart={this._handleTouch.bind(this)}
                />
                <PreviewElementActions
                    onRemove={onRemove}
                    onReplace={onReplace}
                    onCrop={onCrop}
                    showActions={this._showActions()}
                />
            </div>
        );
    }
}
